import * as React from 'react';
import { css, StyleSheet } from 'aphrodite';
import styles from 'login-components/Common/svg/SVGStyles';
import { colors, combineStyles, fonts, media, mixins } from '@blend/consumer-components';
import { WindowWrapper } from 'login-utils/globalWrappers';
import ExternalImage from 'login-components/ExternalImage/ExternalImage';

const ACTION_COLOR = WindowWrapper.DEPLOYMENT_CONFIG.organization.colors.action;

const productCardStyles = StyleSheet.create({
  root: {
    width: 560,
    [media.mobileMedium]: {
      width: '100%',
    },
  },
  card: combineStyles(mixins.border(2, 'solid', colors.white), mixins.borderRadius(10), {
    alignItems: 'center',
    backgroundColor: colors.white,
    boxShadow: mixins.boxShadow(0, 0, 6, 0, mixins.rgba(0, 0, 0, 0.24)),
    display: 'flex',
    marginBottom: 20,
    minHeight: 157,
    width: '100%',
    [media.mobileMedium]: {
      alignItems: 'stretch',
      minHeight: 'inherit',
      paddingBottom: 20,
      paddingTop: 15,
    },
  }),
  // required for IE flexbox bug
  cardFlexWrapper: {
    justifyContent: 'space-between',
    display: 'flex',
  },
  actionButton: {
    ...mixins.removeButtonStyling(),
    ...fonts.Gotham,
    ...mixins.borderRadius(4),
    color: colors.white,
    height: 34,
    fontSize: 14,
    fontWeight: fonts.weight.medium,
    letterSpacing: 0,
    marginBottom: 19,
    backgroundColor: ACTION_COLOR,
    width: 125,
    ':hover': { color: colors.white },
    ':focus': { color: colors.white },
    [media.mobileMedium]: {
      height: 30,
      fontSize: 12,
      marginTop: 0,
      width: 70,
    },
  },
  linkButton: {
    ...mixins.removeButtonStyling(),
    ...fonts.Gotham,
    background: 'none',
    color: ACTION_COLOR,
    fontSize: 12,
    fontWeight: fonts.weight.medium,
    letterSpacing: 0,
    textDecoration: 'underline',
    whiteSpace: 'nowrap',
    [media.mobileMedium]: {
      ...fonts.Roboto,
      fontSize: 14,
      fontWeight: fonts.weight.normal,
      letterSpacing: 0, // need to re-specify after updating font mixin
    },
  },
  cardTitle: {
    color: colors.darkerGray,
    fontSize: 18,
    fontWeight: fonts.weight.bold,
    marginBottom: 4,
  },
  cardDescription: {
    color: colors.clayPlus3,
    fontWeight: fonts.weight.book,
    fontSize: 14,
    paddingTop: 5,
  },
  cardLeftWithImage: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  cardColumnLeft: {
    ...fonts.Roboto,
    marginLeft: 28,
    marginRight: 55,
    textAlign: 'left',
    [media.mobileMedium]: {
      marginLeft: 16,
      marginRight: 19,
    },
  },
  cardColumnRight: {
    display: 'flex',
    flexDirection: 'column',
    marginRight: 32,
    textAlign: 'center',
    [media.mobileMedium]: {
      marginRight: 15,
    },
  },
});

export interface ProductCardBase {
  productId: string;
  title: string;
  description: string;
  isDisabled: boolean;
  hasDetails: boolean;
  modalTitle: string;
  modalContent: string;
  modalNotes: string[];
  imageLink: string | null;
}

interface ProductCardProps extends ProductCardBase {
  onSubmit(Event): Promise<void>;
  openModal(Event): void;
  defaultSVG: React.ComponentType | null;
  className?: string;
}

export interface ProductCardConfig extends ProductCardBase {
  value: any;
}

export default class ProductCard extends React.PureComponent<ProductCardProps> {
  render() {
    const {
      productId,
      title,
      description,
      hasDetails,
      isDisabled,
      imageLink,
      onSubmit,
      openModal,
      defaultSVG,
    } = this.props;
    return (
      <div className={css(productCardStyles.cardFlexWrapper)}>
        <div className={css(productCardStyles.card)}>
          <div className={css(productCardStyles.cardLeftWithImage)}>
            <ExternalImage
              imageLink={imageLink}
              defaultSVG={defaultSVG}
              dataTag={`${productId}-productIcon`}
              className={css(styles.box)}
              imageAlt={`${title} product icon`}
            />
            <div className={css(productCardStyles.cardColumnLeft)}>
              <h2 className={css(productCardStyles.cardTitle)}>{title}</h2>
              <p className={css(productCardStyles.cardDescription)}>{description}</p>
            </div>
          </div>
          <div className={css(productCardStyles.cardColumnRight)}>
            <button
              data-it={`${productId}-applyButton`}
              onClick={onSubmit}
              className={css(productCardStyles.actionButton)}
              disabled={isDisabled}
            >
              Apply
            </button>
            {hasDetails && (
              <button
                data-it={`${productId}-viewDetailsButton`}
                className={css(productCardStyles.linkButton)}
                onClick={openModal}
              >
                View details
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }
}
